import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { QRCodeCanvas } from 'qrcode.react'
import {
    Box,
    Card,
    CardContent,
    Container,
    Typography,
    Button,
    Chip,
    Stack,
    Grid,
    Switch,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Divider,
} from '@mui/material'
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded'
import AddCardRoundedIcon from '@mui/icons-material/AddCardRounded'
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import QrCodeScannerRoundedIcon from '@mui/icons-material/QrCodeScannerRounded'
import PointOfSaleRoundedIcon from '@mui/icons-material/PointOfSaleRounded'
import QrCode2RoundedIcon from '@mui/icons-material/QrCode2Rounded'
import EventRoundedIcon from '@mui/icons-material/EventRounded'
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded'
import SyncRoundedIcon from '@mui/icons-material/SyncRounded'
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { useAuth } from '../../contexts/AuthContext'
import { useLanguage } from '../../contexts/LanguageContext'
import { fetchMerchantInvoices } from '../../api/merchantInvoices'
import api from '../../services/api'

const formatAmount = (value, currency = 'USD') =>
    `${currency} ${Number(value || 0).toFixed(2)}`

const currencyForCountry = (country) => {
    const normalized = String(country || '').trim().toUpperCase()
    if (['US', 'USA', 'UNITED STATES'].includes(normalized)) return 'USD'
    if (['CA', 'CANADA'].includes(normalized)) return 'CAD'
    if (['GB', 'UK', 'UNITED KINGDOM'].includes(normalized)) return 'GBP'
    if (['FR', 'FRANCE', 'EU', 'EUROPE'].includes(normalized)) return 'EUR'
    if (['AU', 'AUSTRALIA'].includes(normalized)) return 'AUD'
    if (['SL', 'SIERRA LEONE'].includes(normalized)) return 'SLE'
    if (['GN', 'GUINEA'].includes(normalized)) return 'GNF'
    if (['LR', 'LIBERIA'].includes(normalized)) return 'LRD'
    if (['GM', 'GAMBIA', 'THE GAMBIA'].includes(normalized)) return 'GMD'
    return 'USD'
}

const normalizeInvoice = (invoice) => ({
    id: String(invoice.id),
    invoiceCode: invoice.invoice_no || invoice.invoiceCode || `INV-${invoice.id}`,
    paymentRef: invoice.payment_reference || invoice.paymentRef || invoice.invoice_no || '',
    createdAt: invoice.created_at || invoice.createdAt || new Date().toISOString(),
    status: invoice.status || 'draft',
    amount: Number(invoice.total ?? invoice.amount ?? 0),
    total: Number(invoice.total ?? invoice.amount ?? 0),
    currency: invoice.currency || 'USD',
    customer_name: invoice.customer_name || '',
    customer_email: invoice.customer_email || '',
    customer_phone: invoice.customer_phone || '',
    customer_contact: invoice.customer_email || invoice.customer_phone || '',
    invoice_title: invoice.invoice_title || invoice.items?.[0]?.description || invoice.invoice_no || 'Invoice',
    description: invoice.note || invoice.items?.[0]?.description || '',
    note: invoice.note || '',
    due_date: invoice.due_date || '',
    public_token: invoice.public_token || '',
    pay_url: invoice.pay_url || (invoice.public_token ? `/pay/invoice/${invoice.public_token}` : ''),
})

export default function MerchantInvoices() {
    const navigate = useNavigate()
    const { user } = useAuth()
    const { t, lang } = useLanguage()
    const isFr = lang === 'fr'

    const isBusiness = useMemo(() => {
        const role = (user?.role || user?.type || '').toLowerCase()
        return (
            role === 'merchant' ||
            role === 'business' ||
            user?.account_type === 'business' ||
            user?.is_business === true ||
            !!user?.business_name
        )
    }, [user])

    const [rows, setRows] = useState([])
    const [wallets, setWallets] = useState([])
    const [primaryCurrency, setPrimaryCurrency] = useState('')
    const [dashboardSummary, setDashboardSummary] = useState(null)
    const [merchantEvents, setMerchantEvents] = useState([])
    const [dashboardLoading, setDashboardLoading] = useState(false)
    const [copiedEventCode, setCopiedEventCode] = useState('')
    const [selectedEvent, setSelectedEvent] = useState(null)

    useEffect(() => {
        let mounted = true

        async function load() {
            try {
                const response = await fetchMerchantInvoices()
                const data = Array.isArray(response?.data) ? response.data.map(normalizeInvoice) : []
                if (!mounted) return
                setRows(data)
            } catch (e) {
                if (!mounted) return
                setRows([])
            } finally {
                if (!mounted) return
            }
        }

        if (isBusiness) load()
        else {
            setRows([])
        }

        return () => {
            mounted = false
        }
    }, [isBusiness, t])

    useEffect(() => {
        let mounted = true
        let intervalId = null

        async function loadDashboardData() {
            setDashboardLoading(true)
            try {
                const [response, dashboardResponse] = await Promise.all([
                    api.get('/wallet/summary'),
                    api.get('/merchant/dashboard/summary'),
                ])
                const eventsResponse = await api.get('/merchant/events').catch(() => ({ data: { data: [] } }))
                const data = response.data?.wallets || response.data?.data?.wallets || []
                if (mounted) {
                    setWallets(Array.isArray(data) ? data : [])
                    setPrimaryCurrency(
                        String(response.data?.primary_currency || response.data?.data?.primary_currency || '').toUpperCase()
                    )
                    setDashboardSummary(dashboardResponse.data || {})
                    setMerchantEvents(Array.isArray(eventsResponse.data?.data) ? eventsResponse.data.data : [])
                }
            } catch (e) {
                if (mounted) {
                    setWallets([])
                    setPrimaryCurrency('')
                    setDashboardSummary({})
                    setMerchantEvents([])
                }
            } finally {
                if (mounted) setDashboardLoading(false)
            }
        }

        const handleFocus = () => {
            if (isBusiness) loadDashboardData()
        }

        if (isBusiness) {
            loadDashboardData()
            intervalId = window.setInterval(loadDashboardData, 60000)
            window.addEventListener('focus', handleFocus)
            document.addEventListener('visibilitychange', handleFocus)
        } else {
            setWallets([])
            setDashboardSummary(null)
        }

        return () => {
            mounted = false
            if (intervalId) window.clearInterval(intervalId)
            window.removeEventListener('focus', handleFocus)
            document.removeEventListener('visibilitychange', handleFocus)
        }
    }, [isBusiness])

    const summary = useMemo(() => {
        const paid = rows.filter((item) => String(item.status).toLowerCase() === 'paid')
        const sent = rows.filter((item) => String(item.status).toLowerCase() === 'sent')
        const overdue = rows.filter((item) => String(item.status).toLowerCase() === 'overdue')
        const outstanding = rows.filter(
            (item) => String(item.status).toLowerCase() !== 'paid'
        )

        const totalCollected = paid.reduce(
            (sum, item) => sum + Number(item.total || 0),
            0
        )
        const totalOutstanding = outstanding.reduce(
            (sum, item) => sum + Number(item.total || 0),
            0
        )

        return {
            paidCount: paid.length,
            sentCount: sent.length,
            overdueCount: overdue.length,
            totalCollected,
            totalOutstanding,
        }
    }, [rows])

    const quickActions = [
        {
            icon: <PointOfSaleRoundedIcon />,
            title: 'POS',
            subtitle: isFr ? 'Accepter les paiements locaux' : 'Accept local payments',
            onClick: () => navigate('/merchant/pos'),
        },
        {
            icon: <AddCardRoundedIcon />,
            title: isFr ? 'Creer facture' : 'Create Invoice',
            subtitle: isFr ? 'Facturer les clients' : 'Bill customers',
            onClick: () => navigate('/invoices/create'),
        },
        {
            icon: <QrCode2RoundedIcon />,
            title: 'QR/NFC',
            subtitle: isFr ? 'QR et tag NFC' : 'QR and NFC tag',
            onClick: () => navigate('/merchant/receive-qr'),
        },
        {
            icon: <QrCodeScannerRoundedIcon />,
            title: isFr ? 'Scanner billet' : 'Scan Ticket',
            subtitle: isFr ? 'Validation a l entree' : 'Gate validation',
            onClick: () => navigate('/merchant/scan-ticket'),
        },
        {
            icon: <EventRoundedIcon />,
            title: isFr ? 'Creer evenement' : 'Create Event',
            subtitle: isFr ? 'Vendre des billets' : 'Sell tickets',
            onClick: () => navigate('/merchant/create-event'),
        },
        {
            icon: <BadgeRoundedIcon />,
            title: isFr ? 'Paie et personnel' : 'Payroll & Workforce',
            subtitle: isFr ? 'Employes et paiements' : 'Employees and payroll',
            onClick: () => navigate('/merchant/payroll-workforce'),
        },
        {
            icon: <HistoryRoundedIcon />,
            title: isFr ? 'Transactions' : 'Transactions',
            subtitle: isFr ? 'Voir les reglements' : 'View settlement log',
            onClick: () => navigate('/transactions'),
        },
        {
            icon: <GroupsRoundedIcon />,
            title: isFr ? 'Clients facture' : 'Invoice Clients',
            subtitle: isFr ? 'Suivre les payeurs facture' : 'Manage invoice clients',
            onClick: () => navigate('/invoices'),
        },
    ]

    const businessCurrency =
        primaryCurrency ||
        dashboardSummary?.today_sales_currency ||
        currencyForCountry(user?.business_country || user?.businessCountry || user?.country || user?.profile?.country)
    const eventPaymentCode = (event) => {
        const fromApi = String(event?.payment_code || '').trim().toUpperCase()
        if (fromApi) return fromApi
        const country = String(event?.country || user?.business_country || user?.businessCountry || user?.country || 'US')
            .replace(/[^a-z0-9]/gi, '')
            .slice(0, 2)
            .toUpperCase() || 'US'
        return `MP-${country}-EVT-${event?.id}`
    }
    const eventPaymentLink = (event) => {
        const fromApi = String(event?.payment_link || event?.paymentLink || '').trim()
        if (fromApi) return fromApi
        const code = eventPaymentCode(event)
        return `${window.location.origin}/tickets?event=${encodeURIComponent(event?.id || '')}&code=${encodeURIComponent(code)}`
    }
    const eventQrPayload = (event) => String(event?.qr_payload || event?.qrPayload || eventPaymentLink(event))
    const eventRevenue = (event) => Number(event?.revenue ?? event?.organizer_earnings ?? event?.organizerEarnings ?? 0)
    const copyEventText = async (event, mode) => {
        const text = mode === 'link' ? eventPaymentLink(event) : eventPaymentCode(event)
        try {
            await navigator.clipboard.writeText(text)
            setCopiedEventCode(`${event?.id}-${mode}`)
            window.setTimeout(() => setCopiedEventCode(''), 2200)
        } catch {
            setCopiedEventCode('')
        }
    }
    const shareEvent = async (event) => {
        const title = event?.title || 'MojoPayr event ticket'
        const code = eventPaymentCode(event)
        const link = eventPaymentLink(event)
        const text = `Buy tickets for ${title} with MojoPayr.\nPayment code: ${code}\n${link}`
        if (navigator.share) {
            try {
                await navigator.share({ title, text, url: link })
                return
            } catch {
                // Fall through to clipboard so the merchant still gets a usable link.
            }
        }
        await copyEventText(event, 'link')
    }
    const downloadEventQr = (event) => {
        const code = eventPaymentCode(event)
        const canvas = document.getElementById(`event-qr-${code}`)
        if (!canvas) return
        const anchor = document.createElement('a')
        anchor.href = canvas.toDataURL('image/png')
        anchor.download = `${code}-qr.png`
        anchor.click()
    }
    const viewEventQr = (event) => {
        setSelectedEvent(event)
        window.setTimeout(() => {
            document.getElementById('event-payment-tools')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }, 100)
    }
    const emailEventLink = (event) => {
        const title = event?.title || 'MojoPayr event ticket'
        const code = eventPaymentCode(event)
        const link = eventPaymentLink(event)
        const subject = encodeURIComponent(`Ticket payment for ${title}`)
        const body = encodeURIComponent(
            `Please use this MojoPayr ticket payment code: ${code}\n\nOpen this link to buy the ticket:\n${link}`
        )
        window.location.href = `mailto:?subject=${subject}&body=${body}`
    }
    const summaryNumber = (key) => Number(dashboardSummary?.[key] || 0)
    const summaryString = (key, fallback) => {
        const value = String(dashboardSummary?.[key] || '').trim()
        return value || fallback
    }

    const summaryCards = [
        {
            label: isFr ? 'Ventes du jour' : "Today's Sales",
            value:
                summaryNumber('today_sales_amount') > 0
                    ? formatAmount(summaryNumber('today_sales_amount'), businessCurrency)
                    : isFr ? 'Pret a vendre' : 'Ready to sell',
            note: isFr ? 'Les ventes du jour apparaitront ici' : "Today's sales will appear here",
        },
        {
            label: isFr ? 'Retraits en attente' : 'Pending Withdrawals',
            value:
                summaryNumber('pending_withdrawals_count') > 0
                    ? String(summaryNumber('pending_withdrawals_count'))
                    : isFr ? 'Aucun' : 'None',
            note: isFr ? 'Aucune demande de paiement' : 'No payout requests yet',
        },
        {
            label: isFr ? 'Service le plus vendu' : 'Top Selling Service',
            value: summaryString('top_selling_service_name', isFr ? 'Aucune vente' : 'No sales yet'),
            note: isFr ? 'Le plus utilise maintenant' : 'Most used right now',
        },
        {
            label: isFr ? 'Revenu billets' : 'Ticket Revenue',
            value: formatAmount(summaryNumber('ticket_revenue_amount'), businessCurrency),
            note: isFr ? 'Tous les evenements marchands' : 'Across merchant events',
        },
        {
            label: isFr ? 'Retire ce mois' : 'Withdrawn This Month',
            value: formatAmount(summaryNumber('withdrawn_this_month_amount'), businessCurrency),
            note: isFr ? 'Reglements marchands termines' : 'Completed merchant settlements',
        },
        {
            label: isFr ? 'Rail paiement favori' : 'Favorite Payout Rail',
            value: summaryString('favorite_payout_rail', isFr ? 'Aucun paiement' : 'No payouts yet'),
            note: isFr ? 'Enregistrez les methodes de paiement' : 'Save payout methods to build history',
        },
        {
            label: isFr ? 'Factures ouvertes' : 'Open Invoices',
            value: String(summaryNumber('open_invoices_count')),
            note: `${summaryNumber('paid_invoices_count')} ${isFr ? 'deja payees' : 'already paid'}`,
        },
        {
            label: isFr ? 'Clients facture' : 'Invoice Clients',
            value: String(summaryNumber('invoice_clients_count')),
            note: `${summaryNumber('recurring_plans_count')} ${isFr ? 'plans recurrents' : 'recurring plans'}`,
        },
    ]

    const recentRows = Array.isArray(dashboardSummary?.recent_transactions)
        ? dashboardSummary.recent_transactions.slice(0, 4)
        : []
    const visibleWallets = wallets
        .map((wallet) => ({
            currency: String(wallet.currency || 'USD').toUpperCase(),
            balance: Number(wallet.available_balance ?? wallet.balance ?? 0),
            pending: Number(wallet.pending_balance ?? 0),
            isPrimary: wallet.is_primary === true || String(wallet.currency || '').toUpperCase() === businessCurrency,
        }))
        .filter((wallet) => wallet.isPrimary || wallet.balance !== 0 || wallet.pending !== 0)
        .sort((a, b) => (a.currency === businessCurrency ? -1 : b.currency === businessCurrency ? 1 : 0))
    const primaryWallet =
        visibleWallets.find((wallet) => wallet.currency === businessCurrency) ||
        visibleWallets.find((wallet) => wallet.isPrimary) ||
        visibleWallets[0] ||
        { currency: businessCurrency, balance: 0, pending: 0, isPrimary: true }
    const alertCount = summary.overdueCount + summary.sentCount
    const recentActivity = recentRows.map((tx) => ({
        title: tx.type || tx.reference || (isFr ? 'Transaction' : 'Transaction'),
        subtitle: tx.status || tx.reference || '',
        amount: formatAmount(tx.amount, tx.currency || businessCurrency),
    }))
    const serviceSnapshot = [
        {
            title: isFr ? 'Billets' : 'Tickets',
            value: isFr ? 'Aucun' : 'None',
            note: isFr ? '0 evenement' : '0 events',
        },
    ]

    return (
        <>
        <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
            <Stack spacing={3}>
                <Card
                    sx={{
                        borderRadius: 6,
                        overflow: 'hidden',
                        background:
                            'linear-gradient(135deg, rgba(42,27,102,1) 0%, rgba(91,46,255,1) 100%)',
                        boxShadow: '0 28px 60px rgba(49,26,137,0.38)',
                    }}
                >
                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                        <Stack spacing={2.5}>
                            <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                justifyContent="space-between"
                                spacing={2}
                            >
                                <Box>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.74)', mb: 1 }}>
                                        {isFr ? 'Portefeuille entreprise' : 'Business Wallet'}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: '#FFFFFF',
                                            fontWeight: 900,
                                            fontSize: { xs: 30, md: 42 },
                                            letterSpacing: '-0.03em',
                                        }}
                                    >
                                        {user?.business_name || user?.name || 'MojoPayr Business'}
                                    </Typography>
                                    <Typography sx={{ color: 'rgba(255,255,255,0.78)', mt: 1 }}>
                                        {isFr
                                            ? 'Suivez vos encaissements, factures et preuves de paiement dans une vue plus legere pour les operations quotidiennes.'
                                            : 'Track collections, invoices, and payment proof in a lighter view built for daily business operations.'}
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    <Chip
                                        label={`${isFr ? 'Solde' : 'Wallet'} ${formatAmount(primaryWallet.balance, primaryWallet.currency)}`}
                                        sx={{ color: '#FFF', bgcolor: 'rgba(255,255,255,0.14)' }}
                                    />
                                    <Chip
                                        label={`${isFr ? 'Encaisse' : 'Collected'} ${formatAmount(summary.totalCollected)}`}
                                        sx={{ color: '#FFF', bgcolor: 'rgba(255,255,255,0.14)' }}
                                    />
                                    <Chip
                                        label={`${rows.length} ${isFr ? 'factures actives' : 'active invoices'}`}
                                        sx={{ color: '#FFF', bgcolor: 'rgba(255,255,255,0.14)' }}
                                    />
                                    <Chip
                                        label={`${summary.overdueCount} ${isFr ? 'en retard' : 'overdue'}`}
                                        sx={{ color: '#FFF', bgcolor: 'rgba(255,255,255,0.14)' }}
                                    />
                                </Stack>
                            </Stack>

                            <Box
                                sx={{
                                    border: '1px solid rgba(255,255,255,0.16)',
                                    bgcolor: 'rgba(255,255,255,0.07)',
                                    borderRadius: 4,
                                    p: 2,
                                }}
                            >
                                <Typography sx={{ color: 'rgba(255,255,255,0.76)', fontWeight: 800, mb: 1 }}>
                                    {isFr ? 'Soldes wallet' : 'Wallet Balances'}
                                </Typography>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    {(visibleWallets.length ? visibleWallets : [primaryWallet]).map((wallet) => (
                                        <Chip
                                            key={wallet.currency}
                                            label={`${wallet.currency} ${Number(wallet.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                                            sx={{ color: '#fff', bgcolor: 'rgba(11,6,32,0.28)', fontWeight: 900 }}
                                        />
                                    ))}
                                </Stack>
                                <Typography sx={{ color: 'rgba(255,255,255,0.64)', fontSize: 13, mt: 1 }}>
                                    {isFr
                                        ? 'La paie utilise le wallet dans la meme devise que les salaires.'
                                        : 'Payroll uses the wallet in the same currency as the employee salaries.'}
                                </Typography>
                            </Box>

                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                                <Button
                                    variant="contained"
                                    startIcon={<ReceiptLongRoundedIcon />}
                                    onClick={() => navigate('/wallet')}
                                    sx={{
                                        bgcolor: '#FFFFFF',
                                        color: '#0B0620',
                                        fontWeight: 900,
                                        borderRadius: 4,
                                        textTransform: 'none',
                                        px: 3,
                                    }}
                                >
                                    {isFr ? 'Retirer' : 'Withdraw'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<ReceiptLongRoundedIcon />}
                                    onClick={() => navigate('/transactions')}
                                    sx={{
                                        color: '#FFFFFF',
                                        borderColor: 'rgba(183,166,255,0.34)',
                                        fontWeight: 800,
                                        borderRadius: 4,
                                        textTransform: 'none',
                                        px: 3,
                                    }}
                                >
                                    {isFr ? 'Voir paiements' : 'View Payouts'}
                                </Button>
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>

                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card
                            sx={{
                                borderRadius: 6,
                                background: 'rgba(27,16,64,0.96)',
                                border: '1px solid rgba(183,166,255,0.16)',
                            }}
                        >
                            <CardContent sx={{ p: 3 }}>
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    justifyContent="space-between"
                                    spacing={2}
                                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                                >
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Box
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                borderRadius: 3,
                                                display: 'grid',
                                                placeItems: 'center',
                                                background: 'rgba(91,46,255,0.28)',
                                                color: '#FF8A00',
                                            }}
                                        >
                                            <SyncRoundedIcon />
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontWeight: 800 }}>
                                                Merchant Sync Hub
                                            </Typography>
                                            <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                                {isFr ? 'Aucun element hors ligne en attente' : 'No offline items waiting'}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                    <Switch
                                        disabled
                                        checked={false}
                                        sx={{
                                            '& .MuiSwitch-switchBase.Mui-checked': {
                                                color: '#FF8A00',
                                            },
                                        }}
                                    />
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <Card
                            onClick={() => navigate('/invoices')}
                            sx={{
                                borderRadius: 6,
                                background: 'rgba(27,16,64,0.96)',
                                border: '1px solid rgba(183,166,255,0.16)',
                                cursor: 'pointer',
                            }}
                        >
                            <CardContent sx={{ p: 3 }}>
                                <Stack direction="row" alignItems="center" spacing={1.8}>
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 3,
                                            display: 'grid',
                                            placeItems: 'center',
                                            background: 'rgba(91,46,255,0.28)',
                                            color: '#FF8A00',
                                        }}
                                    >
                                        <GroupsRoundedIcon />
                                    </Box>
                                    <Typography sx={{ color: 'text.secondary', flex: 1 }}>
                                        {isFr
                                            ? 'Les profils clients apparaitront automatiquement depuis les factures.'
                                            : 'Customer profiles will appear here automatically from invoice activity.'}
                                    </Typography>
                                    <ChevronRightRoundedIcon sx={{ color: 'rgba(255,255,255,0.55)' }} />
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <Card
                            sx={{
                                borderRadius: 6,
                                background: 'rgba(27,16,64,0.96)',
                                border: '1px solid rgba(183,166,255,0.16)',
                            }}
                        >
                            <CardContent sx={{ p: 3 }}>
                                <Typography sx={{ fontWeight: 800, mb: 2.5 }}>
                                    {isFr ? 'Actions rapides' : 'Quick Actions'}
                                </Typography>
                                <Grid container spacing={2}>
                                    {quickActions.map((action) => (
                                        <Grid item xs={6} sm={6} lg={3} key={action.title}>
                                            <Box
                                                onClick={action.onClick}
                                                sx={{
                                                    p: 2.2,
                                                    borderRadius: 5,
                                                    cursor: 'pointer',
                                                    background: 'rgba(91,46,255,0.10)',
                                                    border: '1px solid rgba(183,166,255,0.16)',
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 48,
                                                        height: 48,
                                                        borderRadius: 3,
                                                        display: 'grid',
                                                        placeItems: 'center',
                                                        mb: 2,
                                                        background: 'rgba(91,46,255,0.28)',
                                                        color: '#FF8A00',
                                                    }}
                                                >
                                                    {action.icon}
                                                </Box>
                                                <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
                                                    {action.title}
                                                </Typography>
                                                <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                                    {action.subtitle}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <Card
                            sx={{
                                borderRadius: 6,
                                background: 'rgba(27,16,64,0.96)',
                                border: '1px solid rgba(183,166,255,0.16)',
                            }}
                        >
                            <CardContent sx={{ p: 3 }}>
                                <Stack
                                    direction={{ xs: 'column', md: 'row' }}
                                    justifyContent="space-between"
                                    spacing={2}
                                    alignItems={{ xs: 'flex-start', md: 'center' }}
                                >
                                    <Box>
                                        <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
                                            {isFr ? 'Boite marchande' : 'Merchant Inbox'}
                                        </Typography>
                                        <Typography sx={{ color: 'text.secondary' }}>
                                            {alertCount === 0
                                                ? (isFr
                                                    ? 'Aucune alerte pour le moment sur les encaissements, la conformite ou l activite.'
                                                    : 'No alerts yet across collections, compliance, or activity.')
                                                : (isFr
                                                    ? `${alertCount} alertes sur les encaissements, la conformite et l activite.`
                                                    : `${alertCount} alerts across collections, compliance, and activity.`)}
                                        </Typography>
                                    </Box>
                                    <Button
                                        variant="outlined"
                                        onClick={() => navigate('/notifications')}
                                        sx={{
                                            color: '#FFFFFF',
                                            borderColor: 'rgba(183,166,255,0.34)',
                                            borderRadius: 4,
                                            textTransform: 'none',
                                            fontWeight: 800,
                                        }}
                                    >
                                        {isFr ? 'Ouvrir boite' : 'Open Inbox'}
                                    </Button>
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12}>
                        <Card
                            sx={{
                                borderRadius: 6,
                                height: '100%',
                                background: 'rgba(27,16,64,0.96)',
                                border: '1px solid rgba(183,166,255,0.16)',
                            }}
                        >
                            <CardContent sx={{ p: 3 }}>
                                <Typography sx={{ fontWeight: 800, mb: 2 }}>
                                    {isFr ? 'Reglement et resume' : 'Settle & Summary'}
                                </Typography>
                                {dashboardLoading && (
                                    <LinearProgress
                                        sx={{
                                            mb: 2,
                                            bgcolor: 'rgba(255,255,255,0.08)',
                                            '& .MuiLinearProgress-bar': { bgcolor: '#FF8A00' },
                                        }}
                                    />
                                )}
                                <Grid container spacing={1.5}>
                                    {summaryCards.map((card) => (
                                        <Grid item xs={12} sm={6} md={3} key={card.label}>
                                            <Box
                                                sx={{
                                                    p: 1.8,
                                                    borderRadius: 4,
                                                    bgcolor: 'rgba(91,46,255,0.10)',
                                                    border: '1px solid rgba(183,166,255,0.16)',
                                                    height: '100%',
                                                }}
                                            >
                                                <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                                    {card.label}
                                                </Typography>
                                                <Typography sx={{ fontWeight: 900, fontSize: 22, mt: 0.8 }}>
                                                    {card.value}
                                                </Typography>
                                                <Typography sx={{ color: 'text.secondary', fontSize: 13, mt: 0.75 }}>
                                                    {card.note}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {!isBusiness && (
                    <Card
                        sx={{
                            borderRadius: 6,
                            background: 'rgba(27,16,64,0.96)',
                            border: '1px solid rgba(183,166,255,0.16)',
                        }}
                    >
                        <CardContent sx={{ p: 3 }}>
                            <Typography sx={{ fontWeight: 900 }}>
                                {t(
                                    'invoices.onlyBusiness',
                                    null,
                                    'Invoices are only available for business accounts.'
                                )}
                            </Typography>
                        </CardContent>
                    </Card>
                )}

                {isBusiness && (
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Card
                                sx={{
                                    borderRadius: 6,
                                    background: 'rgba(27,16,64,0.96)',
                                    border: '1px solid rgba(183,166,255,0.16)',
                                    height: '100%',
                                }}
                            >
                                <CardContent sx={{ p: 3 }}>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <Box
                                            sx={{
                                                width: 44,
                                                height: 44,
                                                borderRadius: 3,
                                                display: 'grid',
                                                placeItems: 'center',
                                                background: 'rgba(91,46,255,0.28)',
                                                color: '#FF8A00',
                                            }}
                                        >
                                            <ReceiptLongRoundedIcon />
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontWeight: 800, mb: 0.5 }}>
                                                {isFr ? 'Apercu reglement' : 'Settlement Snapshot'}
                                            </Typography>
                                            <Typography sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                                                {isFr
                                                    ? 'Une fois que l entreprise commence les retraits, MojoPayr suivra les reglements valides et les rails preferes ici.'
                                                    : 'Once the business starts withdrawing, MojoPayr will track settled payouts and preferred rails here.'}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Card
                                sx={{
                                    borderRadius: 6,
                                    background: 'rgba(27,16,64,0.96)',
                                    border: '1px solid rgba(183,166,255,0.16)',
                                    height: '100%',
                                }}
                            >
                                <CardContent sx={{ p: 3 }}>
                                    <Typography sx={{ fontWeight: 800, mb: 1.5 }}>
                                        {isFr ? 'Facturation recurrente' : 'Recurring Billing'}
                                    </Typography>
                                    <Typography sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                                        {isFr
                                            ? 'Aucun plan recurrent pour le moment. Enregistrez des factures comme recurrentes pour lancer la facturation repetitive.'
                                            : 'No recurring plans yet. Save invoices as recurring to build repeat billing.'}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12}>
                            <Card
                                sx={{
                                    borderRadius: 6,
                                    background: 'rgba(27,16,64,0.96)',
                                    border: '1px solid rgba(183,166,255,0.16)',
                                }}
                            >
                                <CardContent sx={{ p: 3 }}>
                                    <Typography sx={{ fontWeight: 800, mb: 1.5 }}>
                                        {isFr ? 'Activite recente' : 'Recent Activity'}
                                    </Typography>
                                    {recentActivity.length === 0 ? (
                                        <Typography sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                                            {isFr
                                                ? 'Aucune activite pour le moment. Les transactions, ventes airtime et recus fiscaux apparaitront ici.'
                                                : 'No activity yet. Transactions, top ups, and tax receipts will show here.'}
                                        </Typography>
                                    ) : (
                                        <Stack spacing={1.2}>
                                            {recentActivity.map((item, index) => (
                                                <Box
                                                    key={`${item.title}-${item.amount}-${index}`}
                                                    sx={{
                                                        p: 1.8,
                                                        borderRadius: 4,
                                                        bgcolor: 'rgba(91,46,255,0.10)',
                                                        border: '1px solid rgba(183,166,255,0.16)',
                                                    }}
                                                >
                                                    <Stack
                                                        direction={{ xs: 'column', sm: 'row' }}
                                                        justifyContent="space-between"
                                                        spacing={1}
                                                    >
                                                        <Box>
                                                            <Typography sx={{ fontWeight: 800 }}>
                                                                {item.title}
                                                            </Typography>
                                                            <Typography sx={{ color: 'text.secondary', fontSize: 13, mt: 0.4 }}>
                                                                {item.subtitle}
                                                            </Typography>
                                                        </Box>
                                                        <Typography sx={{ fontWeight: 900 }}>
                                                            {item.amount}
                                                        </Typography>
                                                    </Stack>
                                                </Box>
                                            ))}
                                        </Stack>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12}>
                            <Card
                                sx={{
                                    borderRadius: 6,
                                    background: 'rgba(27,16,64,0.96)',
                                    border: '1px solid rgba(183,166,255,0.16)',
                                }}
                            >
                                <CardContent sx={{ p: 3 }}>
                                    <Typography sx={{ fontWeight: 800, mb: 1.5 }}>
                                        {isFr ? 'Factures recentes' : 'Recent Invoices'}
                                    </Typography>
                                    {rows.length === 0 ? (
                                        <Typography sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                                            {isFr
                                                ? 'Aucune facture pour le moment. Creez-en une pour partager une demande de paiement.'
                                                : 'No invoices yet. Create one to share a payment request.'}
                                        </Typography>
                                    ) : (
                                        <Stack spacing={1.2}>
                                            {rows.slice(0, 4).map((invoice) => (
                                                <Box
                                                    key={invoice.id}
                                                    onClick={() => navigate(`/invoices/${invoice.id}`)}
                                                    sx={{
                                                        p: 1.8,
                                                        borderRadius: 4,
                                                        bgcolor: 'rgba(91,46,255,0.10)',
                                                        border: '1px solid rgba(183,166,255,0.16)',
                                                        cursor: 'pointer',
                                                    }}
                                                >
                                                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                                                        <Box>
                                                            <Typography sx={{ fontWeight: 800 }}>
                                                                {invoice.invoice_title || invoice.customer_name}
                                                            </Typography>
                                                            <Typography sx={{ color: 'text.secondary', fontSize: 13, mt: 0.4 }}>
                                                                {invoice.customer_name} • {invoice.invoiceCode}
                                                            </Typography>
                                                        </Box>
                                                        <Typography sx={{ fontWeight: 900 }}>
                                                            {formatAmount(invoice.amount, invoice.currency || 'USD')}
                                                        </Typography>
                                                    </Stack>
                                                </Box>
                                            ))}
                                        </Stack>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12}>
                            <Card
                                sx={{
                                    borderRadius: 6,
                                    background: 'rgba(27,16,64,0.96)',
                                    border: '1px solid rgba(183,166,255,0.16)',
                                }}
                            >
                                <CardContent sx={{ p: 3 }}>
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        justifyContent="space-between"
                                        spacing={1}
                                        sx={{
                                            borderTop: '1px solid rgba(255,255,255,0.18)',
                                            pt: 2,
                                            mb: 2,
                                        }}
                                    >
                                        <Box>
                                            <Typography sx={{ fontWeight: 800 }}>
                                                {isFr ? 'Services et performance' : 'Services & Performance'}
                                            </Typography>
                                            <Typography sx={{ color: 'text.secondary', fontSize: 14 }}>
                                                {isFr ? 'S ouvre quand l activite commence' : 'Opens when activity starts'}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                    <Grid container spacing={1.5}>
                                        {serviceSnapshot.map((item) => (
                                            <Grid item xs={12} sm={6} key={item.title}>
                                                <Box
                                                    sx={{
                                                        p: 1.8,
                                                        borderRadius: 4,
                                                        bgcolor: 'rgba(91,46,255,0.10)',
                                                        border: '1px solid rgba(183,166,255,0.16)',
                                                        height: '100%',
                                                    }}
                                                >
                                                    <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                                        {item.title}
                                                    </Typography>
                                                    <Typography sx={{ fontWeight: 900, fontSize: 28, mt: 0.8 }}>
                                                        {item.value}
                                                    </Typography>
                                                    <Typography sx={{ color: 'text.secondary', fontSize: 13, mt: 0.75 }}>
                                                        {item.note}
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid item xs={12}>
                            <Card
                                sx={{
                                    borderRadius: 6,
                                    background: 'rgba(27,16,64,0.96)',
                                    border: '1px solid rgba(183,166,255,0.16)',
                                }}
                            >
                                <CardContent sx={{ p: 3 }}>
                                    <Typography sx={{ fontWeight: 800, mb: 1.5 }}>
                                        {isFr ? 'Performance evenement' : 'Event Performance'}
                                    </Typography>
                                    {merchantEvents.length > 0 ? (
                                        <Stack spacing={1}>
                                            {merchantEvents.slice(0, 6).map((event) => {
                                                const code = eventPaymentCode(event)
                                                const link = eventPaymentLink(event)
                                                const remaining = Number(event?.tickets_remaining ?? event?.tickets_available ?? Math.max(Number(event?.total_tickets || 0) - Number(event?.tickets_sold || 0), 0))
                                                return (
                                                <Box
                                                    key={event.id}
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() => setSelectedEvent(event)}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') setSelectedEvent(event)
                                                    }}
                                                    sx={{
                                                        p: 1.6,
                                                        borderRadius: 4,
                                                        bgcolor: 'rgba(91,46,255,0.10)',
                                                        border: '1px solid rgba(183,166,255,0.16)',
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            borderColor: 'rgba(255,138,0,0.55)',
                                                            bgcolor: 'rgba(91,46,255,0.16)',
                                                        },
                                                    }}
                                                >
                                                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between">
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Typography sx={{ fontWeight: 900 }}>
                                                                {event.title || event.name || 'Event'}
                                                            </Typography>
                                                            <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                                                {formatAmount(event.price, event.currency || businessCurrency)}
                                                                {' | '}
                                                                {isFr ? 'Billets vendus' : 'Tickets sold'} {Number(event.tickets_sold || 0)}
                                                                {' | '}
                                                                {isFr ? 'Restants' : 'Remaining'} {remaining}
                                                            </Typography>
                                                            <Typography sx={{ color: '#FF8A00', fontWeight: 900, fontSize: 13, mt: 1 }}>
                                                                {code}
                                                            </Typography>
                                                            <Typography sx={{ color: 'text.secondary', fontSize: 12, wordBreak: 'break-all' }}>
                                                                {link}
                                                            </Typography>
                                                        </Box>
                                                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                                                            <Box sx={{ bgcolor: '#fff', borderRadius: 2, p: 0.8, lineHeight: 0 }}>
                                                                <QRCodeCanvas value={link} size={82} includeMargin />
                                                            </Box>
                                                            <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); copyEventText(event, 'code') }}>
                                                                {copiedEventCode === `${event.id}-code` ? (isFr ? 'Copie' : 'Copied') : (isFr ? 'Copier code' : 'Copy Code')}
                                                            </Button>
                                                            <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); copyEventText(event, 'link') }}>
                                                                {copiedEventCode === `${event.id}-link` ? (isFr ? 'Copie' : 'Copied') : (isFr ? 'Copier lien' : 'Copy Link')}
                                                            </Button>
                                                            <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); viewEventQr(event) }}>
                                                                {isFr ? 'Voir QR' : 'View QR'}
                                                            </Button>
                                                            <Button size="small" variant="contained" onClick={(e) => { e.stopPropagation(); emailEventLink(event) }}>
                                                                {isFr ? 'Email' : 'Email'}
                                                            </Button>
                                                        </Stack>
                                                    </Stack>
                                                </Box>
                                            )})}
                                        </Stack>
                                    ) : (
                                        <Typography sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                                            {isFr
                                                ? 'Aucun evenement actif dans les donnees serveur.'
                                                : 'No active events in server data.'}
                                        </Typography>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                    </Grid>
                )}
            </Stack>
        </Container>
        <Dialog
            open={Boolean(selectedEvent)}
            onClose={() => setSelectedEvent(null)}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    bgcolor: '#17102f',
                    color: '#fff',
                    borderRadius: 4,
                    border: '1px solid rgba(183,166,255,0.20)',
                },
            }}
        >
            {selectedEvent && (
                <>
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography sx={{ fontWeight: 900, fontSize: 22 }}>
                                {selectedEvent.title || selectedEvent.name || 'Event'}
                            </Typography>
                            <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                {selectedEvent.venue_name || selectedEvent.city || selectedEvent.country || ''}
                            </Typography>
                        </Box>
                        <IconButton onClick={() => setSelectedEvent(null)} sx={{ color: '#fff' }}>
                            <CloseRoundedIcon />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ pt: 0 }}>
                        <Grid container spacing={2.2}>
                            <Grid item xs={12} md={5}>
                                <Box
                                    sx={{
                                        minHeight: 170,
                                        borderRadius: 3,
                                        bgcolor: 'rgba(91,46,255,0.18)',
                                        border: '1px solid rgba(183,166,255,0.18)',
                                        overflow: 'hidden',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {selectedEvent.event_banner_url ? (
                                        <Box
                                            component="img"
                                            src={selectedEvent.event_banner_url}
                                            alt={selectedEvent.title || 'Event banner'}
                                            sx={{ width: '100%', height: 190, objectFit: 'cover' }}
                                        />
                                    ) : (
                                        <EventRoundedIcon sx={{ fontSize: 64, color: '#FF8A00' }} />
                                    )}
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={7}>
                                <Stack spacing={1.2}>
                                    {[
                                        [isFr ? 'Prix billet' : 'Ticket price', formatAmount(selectedEvent.price, selectedEvent.currency || businessCurrency)],
                                        [isFr ? 'Billets vendus' : 'Tickets sold', Number(selectedEvent.tickets_sold || 0)],
                                        [isFr ? 'Billets restants' : 'Remaining tickets', Number(selectedEvent.tickets_remaining ?? selectedEvent.tickets_available ?? Math.max(Number(selectedEvent.total_tickets || 0) - Number(selectedEvent.tickets_sold || 0), 0))],
                                        [isFr ? 'Revenu' : 'Revenue', formatAmount(eventRevenue(selectedEvent), selectedEvent.currency || businessCurrency)],
                                        [isFr ? 'Statut' : 'Status', selectedEvent.status || 'published'],
                                    ].map(([label, value]) => (
                                        <Stack key={label} direction="row" justifyContent="space-between" spacing={2}>
                                            <Typography sx={{ color: 'text.secondary' }}>{label}</Typography>
                                            <Typography sx={{ fontWeight: 900, textAlign: 'right' }}>{value}</Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            </Grid>
                            <Grid item xs={12}>
                                <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)', my: 1 }} />
                                <Typography id="event-payment-tools" sx={{ fontWeight: 900, mb: 1.5 }}>
                                    {isFr ? 'Accepter les paiements' : 'Accept Payments'}
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={4}>
                                        <Box sx={{ bgcolor: '#fff', borderRadius: 3, p: 1.2, width: 180, mx: { xs: 'auto', md: 0 }, lineHeight: 0 }}>
                                            <QRCodeCanvas
                                                id={`event-qr-${eventPaymentCode(selectedEvent)}`}
                                                value={eventQrPayload(selectedEvent)}
                                                size={160}
                                                includeMargin
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} md={8}>
                                        <Stack spacing={1}>
                                            <Box>
                                                <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                                    {isFr ? 'Code de paiement' : 'Event Payment Code'}
                                                </Typography>
                                                <Typography sx={{ color: '#FF8A00', fontWeight: 900, wordBreak: 'break-all' }}>
                                                    {eventPaymentCode(selectedEvent)}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
                                                    {isFr ? 'Lien de paiement' : 'Shareable Payment Link'}
                                                </Typography>
                                                <Typography sx={{ wordBreak: 'break-all' }}>
                                                    {eventPaymentLink(selectedEvent)}
                                                </Typography>
                                            </Box>
                                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                <Button variant="outlined" onClick={() => viewEventQr(selectedEvent)}>{isFr ? 'Voir QR' : 'View QR Code'}</Button>
                                                <Button variant="outlined" onClick={() => downloadEventQr(selectedEvent)}>{isFr ? 'Telecharger QR' : 'Download QR Code'}</Button>
                                                <Button variant="outlined" onClick={() => shareEvent(selectedEvent)}>{isFr ? 'Partager QR' : 'Share QR Code'}</Button>
                                                <Button variant="outlined" onClick={() => copyEventText(selectedEvent, 'code')}>{isFr ? 'Copier code' : 'Copy Payment Code'}</Button>
                                                <Button variant="outlined" onClick={() => copyEventText(selectedEvent, 'link')}>{isFr ? 'Copier lien' : 'Copy Payment Link'}</Button>
                                                <Button variant="contained" onClick={() => shareEvent(selectedEvent)}>{isFr ? 'Partager evenement' : 'Share Event'}</Button>
                                            </Stack>
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </DialogContent>
                </>
            )}
        </Dialog>
        </>
    )
}

